import { useState, useEffect, useRef } from "react";
import './ImageCarousel.css';

const ImageCarousel = ({ images, interval = 5000}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [touchStart, setTouchStart] = useState(0);
    const [touchEnd, setTouchEnd] = useState(0);
    const [loadedImages, setLoadedImages] = useState(new Set([0]));
    const intervalRef = useRef(null);

    const minSwipeDistance = 50;

    useEffect(() => {
        //Preload next and previous images
        const nextIndex = (currentIndex + 1) % images.length;
        const prevIndex = (currentIndex - 1 + images.length) % images.length;
        
        [nextIndex, prevIndex].forEach(index => {
            if(!loadedImages.has(index)) {
                const img = new Image();
                img.src = images[index].src;
                img.onload = () => {
                    setLoadedImages(prev => new Set([...prev, index]));
                };
            }
        });
    }, [currentIndex, images, loadedImages]);

    useEffect(() => {
        intervalRef.current = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
        }, interval);

        //Clear the interval on unmount
        return () => {
            if(intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [images.length, interval]);

    //Reset the timer when the user manually changes slides
    const resetTimer = () => {
        if(intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = setInterval(() => {
                setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
            }, interval);
        }
    };

    //Handle touch events for swiping
    const handleTouchStart = (e) => {
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if(!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if(isLeftSwipe) {
            //Next slide
            setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
            resetTimer();
        } else if(isRightSwipe) {
            //previous slide
            setCurrentIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
            resetTimer();
        }

        //Reset values
        setTouchStart(0);
        setTouchEnd(0);
    };

    //Navigate to specific slide
    const goToSlide = (index) => {
        setCurrentIndex(index);
        resetTimer();
    };

    // Nav to previous slide
    const prevSlide = () => {
        setCurrentIndex((prevIndex) => (prevIndex === 0 ? images.length - 1 : prevIndex - 1));
        resetTimer();
    };

    //Nav to next slide
    const nextSlide = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
        resetTimer();
    };


    return (
        <div className="custom-carousel-container">
            <div
                className="custom-carousel-inner"
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                >
                    {images.map((image, index) => {
                        // Only render slides that have been loaded or are currently visible
                        if (!loadedImages.has(index) && index !== currentIndex) {
                            return null;
                        }
                        
                        return (
                            <div
                                key={index}
                                className={`custom-carousel-item ${index === currentIndex ? 'active' : ''}`}
                                style={{display: index === currentIndex ? 'block' : 'none'}}
                            >
                                <img
                                    src={image.src}
                                    alt={image.alt || `Slide ${index + 1}`}
                                    className="w-100 h-100"
                                    loading={index === 0 ? 'eager' : 'lazy'}
                                    style={{
                                        objectFit: 'cover'
                                    }}
                                />
                                {image.caption && (
                                    <div className="custom-carousel-caption">
                                        <p className="m-0">{image.caption}</p>
                                    </div>
                                )}
                            </div>
                        );
                    })}
            </div>
            {/*Navigation Arrows */}
            <button
                className="custom-carousel-control-prev"
                type="button"
                onClick={prevSlide}
            >
                <span className="custom-carousel-control-prev-icon" aria-hidden="true">
                    <i className="fas fa-chevron-left"></i>
                </span>
                <span className="visually-hidden">Previous</span>
            </button>

            <button
                className="custom-carousel-control-next"
                type="button"
                onClick={nextSlide}
            >
                <span className="custom-carousel-control-next-icon" aria-hidden="true">
                    <i className="fas fa-chevron-right"></i>
                </span>
                <span className="visually-hidden">Next</span>
            </button>

            {/* Indicator dots */}
            <div className="custom-carousel-indicators">
                {images.map((_, index) => (
                    <button
                        key={index}
                        type="button"
                        className={index === currentIndex ? 'active' : ''}
                        onClick={() => goToSlide(index)}
                        aria-label={`Slide ${index + 1}`}
                    />
                ))}
            </div>
        </div>
    );
};

export default ImageCarousel;